import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    Container,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import * as Sentry from '@sentry/react';
import React from 'react';

// Update the interface to match Sentry's expected error structure
interface ErrorFallbackProps {
  error: Error;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

// Update the component to handle the new prop types
const ErrorFallback = ({ error, resetError, eventId }: ErrorFallbackProps) => {
  return (
    <Container maxWidth="md">
      <Box 
        sx={{ 
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h4" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" paragraph>
          We apologize for the inconvenience. Our team has been notified and is working to fix the issue.
        </Typography>
        
        <Paper 
          elevation={1} 
          sx={{ 
            p: 3, 
            width: '100%', 
            mb: 3,
            textAlign: 'left',
            overflow: 'auto'
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Error details:
          </Typography>
          <Typography 
            component="pre" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              m: 0
            }}
          >
            {error.message}
            {eventId && `\nEvent ID: ${eventId}`}
          </Typography>
        </Paper>
        
        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={resetError}
          >
            Try Again
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
}

// Define a type for the Sentry error object
interface SentryError extends Error {
  eventId?: string;
}

const SentryErrorBoundary = Sentry.withErrorBoundary(
  function ErrorBoundaryWrapper({ children }) {
    return <>{children}</>;
  },
  {
    fallback: ErrorFallback,
    beforeCapture: (scope, error, componentStack) => {
      console.log("beforeCapture", scope, error, componentStack);
      // Add any custom metadata if needed
      scope.setExtra("componentStack", componentStack);
      scope.setTag("react", "functional-component");

      // Sentry internally calls captureReactException here
      Sentry.captureReactException(error, {
        componentStack
      });
      // You can't manually call it in functional components
    },
  }
);

export default SentryErrorBoundary;